import React, { useEffect, useMemo, useState } from 'react';
import { FolderOpen, Search, Trash2, X, Loader2, Image as ImageIcon, Film, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

/**
 * Media Library modal: elenca i file già caricati, permette di sceglierli e cancellarli.
 * onPick(url): callback con URL assoluto del file selezionato.
 */
export default function MediaLibrary({ accept, onPick, onClose }) {
  const [files, setFiles] = useState(null);
  const [q, setQ] = useState('');
  const [folder, setFolder] = useState('all');

  const reload = () => api.listMedia().then(setFiles).catch(() => setFiles([]));
  useEffect(() => { reload(); }, []);

  const folders = useMemo(() => {
    const s = new Set();
    (files || []).forEach((f) => s.add(f.folder || 'root'));
    return ['all', ...Array.from(s).sort()];
  }, [files]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let list = files || [];
    if (folder !== 'all') list = list.filter((f) => (f.folder || 'root') === folder);
    // Filtro per tipo in base a `accept`
    if (accept && accept.startsWith('image/')) list = list.filter((f) => f.kind === 'image');
    else if (accept && accept.startsWith('video/')) list = list.filter((f) => f.kind === 'video');
    else if (accept === 'application/pdf') list = list.filter((f) => f.kind === 'pdf');
    if (ql) list = list.filter((f) => f.name.toLowerCase().includes(ql) || (f.folder || '').toLowerCase().includes(ql));
    return list;
  }, [files, q, folder, accept]);

  const del = async (f) => {
    if (!window.confirm(`Eliminare definitivamente "${f.name}"?`)) return;
    try { await api.deleteMedia(f.rel); toast.success('File eliminato'); reload(); }
    catch (e) { toast.error(e?.message); }
  };

  const pick = (f) => {
    // f.url è già normalizzato in `/api/uploads/...` dal client API.
    // Restituisci il path relativo così è portabile tra domini.
    onPick(f.url);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy max-w-5xl w-full max-h-[92vh] overflow-hidden rounded-2xl border border-white/10 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <FolderOpen size={18} className="text-pink-brand" />
            <h3 className="text-white font-bold text-lg">Archivio immagini</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="bg-navy-dark border border-white/20 rounded-lg pl-9 pr-3 py-2 text-white text-sm w-56" />
            </div>
            <select value={folder} onChange={(e) => setFolder(e.target.value)} className="bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm">
              {folders.map((f) => <option key={f} value={f}>{f === 'all' ? 'Tutte le cartelle' : f}</option>)}
            </select>
            <button onClick={onClose} className="p-2 text-white/60 hover:text-pink-brand"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!files ? (
            <div className="text-center py-10"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-white/60 text-sm">
              Nessun file. Carica la prima immagine dal pulsante "Trascina un file" in qualsiasi sezione admin.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {filtered.map((f) => (
                <div key={f.rel} className="group relative bg-navy-dark border border-white/10 rounded-lg overflow-hidden" data-testid={`media-item-${f.name}`}>
                  <button onClick={() => pick(f)} className="block w-full text-left">
                    <div className="aspect-square bg-navy-dark flex items-center justify-center">
                      {f.kind === 'image' && <img src={f.url} alt={f.name} className="w-full h-full object-cover" />}
                      {f.kind === 'video' && <Film size={32} className="text-pink-brand" />}
                      {f.kind === 'pdf' && <FileText size={32} className="text-pink-brand" />}
                      {f.kind === 'file' && <ImageIcon size={32} className="text-white/40" />}
                    </div>
                    <div className="p-2">
                      <div className="text-white text-[11px] font-semibold truncate" title={f.name}>{f.name}</div>
                      <div className="text-white/50 text-[10px]">{f.folder}</div>
                    </div>
                  </button>
                  <button onClick={() => del(f)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-white/10 text-white/50 text-xs text-center">
          {filtered.length > 0 && `${filtered.length} file visibili${files ? ` su ${files.length}` : ''} · clicca su un'immagine per sceglierla`}
        </div>
      </div>
    </div>
  );
}
